import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/images/head.webp";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Tea", path: "/tea" },
    { name: "Coconut", path: "/coconut" },
    { name: "Spices", path: "/spices" },
    { name: "Rice", path: "/rice" },
    { name: "Products", path: "/products" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isDarkBg = !isScrolled && location.pathname === "/";

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 py-3"
          : isDarkBg
          ? "bg-gradient-to-b from-black/70 via-black/40 to-transparent py-4"
          : "bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100 py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={logo}
            alt="Ceymos Lanka"
            className="w-11 h-11 object-contain transition-transform group-hover:scale-105"
          />
          <div className="flex flex-col">
            <span
              className={`text-xl font-bold tracking-wider leading-tight ${
                isDarkBg ? "text-white" : "text-emerald-950"
              }`}
            >
              CEYMOS LANKA
            </span>
            <span
              className={`text-[10px] tracking-widest uppercase font-medium ${
                isDarkBg ? "text-amber-300" : "text-amber-700"
              }`}
            >
              Pure Ceylon Heritage
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `text-[15px] font-semibold transition-colors duration-200 relative py-1 ${
                  isActive
                    ? isDarkBg
                      ? "text-amber-400 font-bold after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-amber-400"
                      : "text-emerald-700 font-bold after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-emerald-600"
                    : isDarkBg
                    ? "text-gray-200 hover:text-white"
                    : "text-gray-700 hover:text-emerald-800"
                }`
              }
            >
              {link.name}
            </NavLink>
          ))}

          <Link
            to="/contact"
            className="ml-2 px-5 py-2 rounded-full text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-sm transition transform hover:-translate-y-0.5"
          >
            Export Inquiry
          </Link>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className={`lg:hidden p-2 rounded-lg text-2xl transition ${
            isDarkBg ? "text-white" : "text-gray-900"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation menu"
        >
          {mobileOpen ? (
            <i className="fas fa-times"></i>
          ) : (
            <i className="fas fa-bars"></i>
          )}
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white border-b border-gray-200 shadow-xl px-6 py-5 space-y-3"
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              end={link.path === "/"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block py-2 text-base font-semibold transition ${
                  isActive
                    ? "text-emerald-700 font-bold pl-2 border-l-4 border-emerald-600"
                    : "text-gray-700 hover:text-emerald-700 pl-2"
                }`
              }
            >
              {link.name}
            </NavLink>
          ))}

          <div className="pt-3 border-t border-gray-100">
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="block text-center py-2.5 rounded-lg bg-amber-600 text-white font-semibold shadow-sm"
            >
              Export Inquiry
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
